<template>
  <UHeader
    :toggle="{ class: 'rounded-full dark:text-(--main-color)' }"
    :ui="{ root: 'sticky top-0 z-50' }"
    class="bg-transparent"
  >
    <template #title>
      <p class="text-(--main-color-accessible) font-bold dark:text-(--main-color)">Logo</p>
    </template>

    <UNavigationMenu :items="items" />

    <template #right>
      <UChip
        v-if="cartStore.totalItems > 0"
        :text="cartStore.totalItems"
        color="main"
        size="3xl"
        position="top-right"
        inset
        :ui="{
          base: 'px-1.5 py-[0.45rem] right-[-4px] top-[-2px] text-black dark:text-white dark:ring-white',
        }"
      >
        <UButton
          color="main"
          variant="ghost"
          icon="i-lucide-shopping-cart"
          aria-label="Ouvrir le panier"
          @click="cartStore.openCart"
        />
      </UChip>
      <UButton
        v-else
        color="main"
        variant="ghost"
        icon="i-lucide-shopping-cart"
        aria-label="Ouvrir le panier"
        @click="cartStore.openCart"
      />
      <UColorModeButton class="rounded-full dark:text-neutral-500">
        <template #fallback>
          <UButton color="neutral" loading variant="ghost" />
        </template>
      </UColorModeButton>
      <UDropdownMenu :items="userDropdownItems" popper>
        <UButton
          class="rounded-full dark:text-neutral-500"
          color="neutral"
          icon="i-heroicons-user"
          variant="ghost"
          aria-label="Menu utilisateur"
        />
      </UDropdownMenu>
    </template>

    <template #body>
      <UNavigationMenu :items="items" class="-mx-2.5" orientation="vertical" />
    </template>
  </UHeader>

  <!-- Cart Drawer -->
  <ClientOnly>
    <CartDrawer />
  </ClientOnly>
  <div
    id="search-zone"
    class="search-zone absolute inset-0 flex items-center justify-center"
  >
    <img
      src="~/assets/images/main-section-_1_.webp"
      alt="Assortiment de plats délicieux préparés par notre restaurant"
      fetchpriority="high"
      decoding="async"
      width="1280"
      height="854"
      class="absolute inset-0 w-full h-full object-cover"
    />
    <div class="absolute inset-0 bg-black/20" />
    <div class="relative mx-auto w-2/3 text-center z-10">
      <UCard
        :ui="{
          root: 'divide-neutral-500 dark:divide-black',
        }"
        class="bg-transparent dark:bg-transparent backdrop-blur-md ring-neutral-600"
        variant="outline"
      >
        <template #header>
          <h1 class="text-5xl text-white dark:text-white font-bold drop-shadow-lg">
            Trouvez le repas de votre choix !
          </h1>
        </template>
        <template #footer>
          <UButton
            :label="'Rechercher un repas'"
            color="main"
            class="sm:w-5/12 w-100% mx-2 my-2 justify-center"
            size="xl"
            @click="isSearchModalOpen = true"
          />
          <UButton
            :label="'Afficher le menu'"
            color="main"
            class="sm:w-5/12 w-100% mx-2 justify-center"
            size="xl"
            to="/menus"
          />
        </template>
      </UCard>
    </div>
  </div>

  <ClientOnly>
    <SearchModal v-model:open="isSearchModalOpen" />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useAuthStore } from "~/stores/auth";
import type { DropdownItem } from "~/models/header";
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const cartStore = useCartStore();
const { $api } = useNuxtApp();
const { errorToast } = useToastMessages();

// Search State
const isSearchModalOpen = ref(false);

onMounted(() => {
  console.log("Nav mounted, search modal open:", isSearchModalOpen.value);
});

const navBaseClass =
  "text-lg px-2.5 py-1.5 dark:text-neutral-600 group relative flex items-center gap-1.5 font-medium before:absolute before:z-[-1] before:rounded-[calc(var(--ui-radius)*1.5)] focus:outline-none focus-visible:outline-none dark:focus-visible:outline-none before:inset-x-px before:inset-y-0 transition-colors before:transition-colors hover:none";
const navInactiveClass =
  "text-(--ui-text-muted) hover:text-(--ui-text-highlighted) before:bg-transparent";
const navActiveClass =
  "text-(--main-color) before:bg-(--ui-bg-elevated) before:ring-2 before:ring-inset before:ring-transparent dark:text-(--main-color)";

const items = computed(() => [
  {
    label: "Repas",
    to: "/repas",
    active: route.path.startsWith("/repas"),
    class: `${navBaseClass} ${
      route.path.startsWith("/repas") ? navActiveClass : navInactiveClass
    }`,
  },
  {
    label: "Menus",
    to: "/menus",
    active: route.path.startsWith("/menus"),
    class: `${navBaseClass} ${
      route.path.startsWith("/menus") ? navActiveClass : navInactiveClass
    }`,
  },
  {
    label: "Boissons",
    to: "/boissons",
    class: `${navBaseClass} ${
      route.path.startsWith("/boisson") ? navActiveClass : navInactiveClass
    }`,
  },
  {
    label: "Desserts",
    to: "desserts",
    class: `${navBaseClass} ${
      route.path.startsWith("/desserts") ? navActiveClass : navInactiveClass
    }`,
  },
  {
    label: "Suivi",
    to: "/track",
    class: `${navBaseClass} ${
      route.path.startsWith("/track") || route.path.startsWith("/orders")
        ? navActiveClass
        : navInactiveClass
    }`,
  },
]);

const userDropdownItems = ref<DropdownItem[]>([]);

const updateDropdownItems = () => {
  if (authStore.isLoggedIn()) {
    userDropdownItems.value = [
      {
        label: "Profile",
        icon: "i-heroicons-user-circle",
        to: "/profile",
      },
      {
        label: "Settings",
        icon: "i-heroicons-cog",
        to: "/settings",
      },
      {
        label: "Logout",
        icon: "i-lucide-log-out",
        onSelect: async () => await logout(),
      },
    ];
  } else {
    userDropdownItems.value = [
      {
        label: "Login",
        icon: "i-lucide-log-in",
        to: "/login",
      },
      {
        label: "Register",
        icon: "i-lucide-user-plus",
        to: "/register",
      },
    ];
  }
};

updateDropdownItems();
watch(
  () => authStore.email, // Watch the email field from the authStore (reactive)
  updateDropdownItems, // Update dropdown whenever email changes
  { immediate: true },
);

const logout = async () => {
  try {
    await $api.post("/user/logout", {}, "include");
    authStore.logout();
    await router.push("/login");
  } catch {
    errorToast(
      "erreur de deconnexion",
      "Impossible de se deconnecter, veuillez ressayer",
    );
  }
};
</script>

<style scoped>
#search-zone {
  height: 50vh;
  position: relative;
  overflow: hidden;
}
</style>
